import type { Figure } from "@howcomet/shared";
import type { FigureProps } from "./registry";

const HEADS = ["var(--color-cyan)", "var(--color-pink)", "var(--color-mint)"];

/**
 * A small grid: column headings, then rows comparing them.
 *
 * The grid is doing the remembering. Read as prose, "reptiles have dry scales
 * and amphibians have moist skin, reptiles lay eggs on land and amphibians lay
 * them in water" asks a child to hold four facts in two columns in their head.
 * Put the columns on the screen and the comparison is just there.
 *
 * `labels` arrives row-major with the headings first — see contrast.ts for why
 * it travels that way rather than as its own field.
 */
export function ContrastFigure({ figure }: FigureProps<Figure>) {
  const cols = figure.cols ?? 2;
  const labels = figure.labels ?? [];
  if (cols < 2 || labels.length < cols * 2) return null;

  const headings = labels.slice(0, cols);
  const body = labels.slice(cols);
  const rows: string[][] = [];
  for (let i = 0; i < body.length; i += cols) rows.push(body.slice(i, i + cols));

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[14px]">
        <thead>
          <tr>
            {headings.map((heading, i) => (
              <th
                key={heading}
                scope="col"
                className="disp rounded-t-lg px-2 py-2 text-center text-[15px] leading-tight text-night"
                style={{ background: HEADS[i % HEADS.length] }}
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td
                  key={`${r}-${c}`}
                  className="border border-lilac/25 px-2 py-2 text-center leading-snug text-cream/90"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
